import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router';

import { useAuth } from '@/lib/auth/contexto';
import { mensagemDeErro } from '@/lib/erros';
import { CampoSenha } from '@/ui/components/campo-senha';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { Marca } from '@/ui/shell/marca';
import { type LoginRequest, loginRequestSchema } from '@fiscoai/contracts';

import { entrarComSenha } from './api';
import { ChaveAnotada } from './chave-anotada';

export function PaginaLogin({ nomeProduto }: { nomeProduto: string }) {
  const { estado, entrar } = useAuth();
  const local = useLocation();
  const destino = (local.state as { de?: string } | null)?.de ?? '/';
  const formulario = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: '', senha: '' },
  });
  const login = useMutation({
    mutationFn: entrarComSenha,
    onSuccess: (sessao) => entrar(sessao),
  });

  if (estado === 'autenticado') return <Navigate to={destino} replace />;
  const erros = formulario.formState.errors;

  return (
    <div className="grid min-h-dvh bg-fundo lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-painel-marca p-12 lg:flex">
        <Marca
          nome={nomeProduto}
          className="text-painel-marca-texto [&>span]:text-painel-marca-texto"
        />
        <div className="grid max-w-xl gap-10">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-painel-marca-texto">
            Toda nota começa pela chave.
          </h1>
          <ChaveAnotada />
          <p className="max-w-md text-lg text-painel-marca-suave">
            O {nomeProduto} junta as notas de todas as empresas, de onde vierem, e mostra o que
            ainda falta conferir.
          </p>
        </div>
        <p className="text-xs text-painel-marca-suave">Acesso restrito à equipe autorizada.</p>
      </section>

      <section className="grid place-items-center px-6 py-12">
        <form
          noValidate
          onSubmit={(evento) => {
            void formulario.handleSubmit((dados) => {
              login.mutate(dados);
            })(evento);
          }}
          className="grid w-full max-w-sm gap-6"
        >
          <div className="grid gap-2">
            <Marca nome={nomeProduto} className="lg:hidden" />
            <h2 className="text-2xl font-semibold tracking-tight">Entrar</h2>
            <p className="text-base text-texto-suave">
              Use o e-mail e a senha cadastrados pela administração.
            </p>
          </div>
          <Campo rotulo="E-mail" erro={erros.email?.message}>
            {(a11y) => (
              <Input
                type="email"
                autoComplete="username"
                autoFocus
                {...a11y}
                {...formulario.register('email')}
              />
            )}
          </Campo>
          <Campo rotulo="Senha" erro={erros.senha?.message}>
            {(a11y) => (
              <CampoSenha
                autoComplete="current-password"
                {...a11y}
                {...formulario.register('senha')}
              />
            )}
          </Campo>
          {login.isError && (
            <p
              role="alert"
              className="rounded-controle bg-perigo-suave px-3 py-2 text-sm text-perigo animate-surgir"
            >
              {mensagemDeErro(login.error)}
            </p>
          )}
          <Button type="submit" variante="primario" carregando={login.isPending} className="w-full">
            Entrar
          </Button>
        </form>
      </section>
    </div>
  );
}
